import React, { useState, useEffect } from "react";
import Header from "components/layout/Header";
import Footer from "components/layout/Footer";
import Sidebar from "components/layout/Sidebar";
import "../layout.css";
import { Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { decrypt } from "customhooks/helper";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "store";
import { setSuspended } from "store/slices/authSlice";
import { apiService } from "services/apiService";
import SuspendedAccountModal from "components/common/SuspendedAccountModal";
import { fetchCreditBalance, fetchAvailableFeatures } from "store/slices/creditsSlice";
import { AppDispatch } from "store";
import './Layout.css';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(true);
  const sidebarWidth = 274;
  const collapsedWidth = 78;

  const toggleSidebar = () => setIsOpen(!isOpen);
  const dispatch = useDispatch<AppDispatch>();
  const state = useSelector((state: RootState) => state.auth);
  const isDomainVerified = state.verified_domain;
  const isSuspended = state.is_suspended;

  const [showSuspendedModal, setShowSuspendedModal] = useState(false);

  // Initialize available features centrally for the whole app
  useEffect(() => {
    dispatch(fetchAvailableFeatures());
  }, [dispatch]);

  // Sync suspension status for already logged-in users on layout mount
  useEffect(() => {
    const checkSuspensionStatus = async () => {
      try {
        const progressRes = await apiService.getSignupProgress();
        if (progressRes?.data?.is_suspended !== undefined && progressRes?.data?.is_suspended !== null) {
          dispatch(setSuspended(!!progressRes.data.is_suspended));
        }
      } catch (err) {
        console.warn("Status sync error:", err);
      }
    };
    checkSuspensionStatus();
  }, [dispatch]);

  const [isBannerDismissed, setIsBannerDismissed] = useState(() => {
    return sessionStorage.getItem("dismissedVerificationBanner") === "true";
  });

  const handleContentClickCapture = (e: React.MouseEvent) => {
    if (isSuspended) {
      const target = e.target as HTMLElement;
      const interactive = target.closest('button, input, select, textarea, form, a, [role="button"], .btn');
      if (interactive) {
        e.preventDefault();
        e.stopPropagation();
        setShowSuspendedModal(true);
      }
    }
  };

  const handleContentSubmitCapture = (e: React.FormEvent) => {
    if (isSuspended) {
      e.preventDefault();
      e.stopPropagation();
      setShowSuspendedModal(true);
    }
  };

  const hasBanner = isSuspended || (!isDomainVerified && !isBannerDismissed);

  return (
    <>
      {isSuspended ? (
        <div className="verification-warning-banner suspended-banner">
          <span className="warning-icon">🚫</span>
          <span>Your account has been suspended. You can view pages from the sidebar, but action operations are disabled.</span>
          <button
            type="button"
            className="verify-btn suspended-btn"
            onClick={() => setShowSuspendedModal(true)}
          >
            Account Status
          </button>
        </div>
      ) : (
        !isDomainVerified && !isBannerDismissed && (
          <div className="verification-warning-banner">
            <span className="warning-icon">⚠️</span>
            <span>Your account is not verified. Please complete domain verification to access all platform features.</span>
            <Link to="/business-verification" className="verify-btn">Verify Now</Link>
            <button
              type="button"
              className="remind-btn"
              onClick={() => {
                sessionStorage.setItem("dismissedVerificationBanner", "true");
                setIsBannerDismissed(true);
              }}
              aria-label="Dismiss banner"
            >
              Remind me later
            </button>
          </div>
        )
      )}

      <div className={`dashboard-layout ${hasBanner ? "has-banner" : ""}`}>


        {/* Sidebar */}
        <Sidebar isOpen={isOpen} setIsOpen={setIsOpen} />

        {/* Main Content */}
        <main
          className={`main-content ${isOpen ? "sidebar-open" : "sidebar-collapsed"} ${hasBanner ? "header-not-verified" : ""}`}
          onClickCapture={handleContentClickCapture}
          onSubmitCapture={handleContentSubmitCapture}
        >
          <Header isDomainVerified={isDomainVerified || isBannerDismissed} />
          <div className="main-content-inner">
            {children}
          </div>
        </main>
      </div>

      <SuspendedAccountModal
        show={showSuspendedModal}
        onHide={() => setShowSuspendedModal(false)}
      />
    </>
  );
};

export default Layout;
